import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import {
  DetailedProduct,
  DetailedSolution,
  DownloadQuarterlyResponse,
  DownloadQuarterlyUpdate,
  MailingListRequest,
  ProductItem,
  SolutionMenuItem
} from "../models/products.model";
import { MemoryCacheService } from "./memory-cache.service";
import { SiilaTranslationService } from "./siila-translation.service";

@Injectable({ providedIn: "root" })
export class ProductService {
  lang: string;
  private readonly PRODUCTS_KEY = "menu:products";
  private readonly SOLUTIONS_KEY = "menu:solutions";

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private siilaTranslationService: SiilaTranslationService,
    private memCacheSrv: MemoryCacheService
  ) {
    this.lang = this.siilaTranslationService.getCurrentLanguage();
  }

  /**
   * Get a list of Products add ons
   */
  getMarketAnalytics(): Observable<ProductItem[]> {
    const currentEnv = environment.platformId.toUpperCase();

    const countryCode = currentEnv.toLowerCase();
    const cacheKey = this.PRODUCTS_KEY + countryCode.toLowerCase();

    const cached = this.memCacheSrv.get<ProductItem[]>(cacheKey);
    if (cached) {
      return cached;
    }

    return this.memCacheSrv.set(
      cacheKey,
      this.http.get<ProductItem[]>(
        `${environment.apiURL}/content/product?groupCode=MARKET_ANALYTICS&countryCode=${currentEnv}`
      )
    );
  }

  getProduct(id: number) {
    return this.http.get<DetailedProduct>(`
    ${environment.apiURL}/content/product/${id}`);
  }

  getSolutionList() {
    const countryCode =
      environment.platformId == "latam"
        ? "LATAM"
        : environment.platformId.toUpperCase();

    const cacheKey = this.SOLUTIONS_KEY + countryCode.toLowerCase();

    const cached = this.memCacheSrv.get<SolutionMenuItem[]>(cacheKey);
    if (cached) {
      return cached;
    }

    return this.memCacheSrv.set(
      cacheKey,
      this.http.get<SolutionMenuItem[]>(
        `${environment.apiURL}/content/solution?countryCode=${countryCode}`
      )
    );
  }

  getSolution(id: number) {
    return this.http.get<DetailedSolution>(
      `${environment.apiURL}/content/solution/${id}`
    );
  }

  requestFile(downloadQuarterlyUpdate: DownloadQuarterlyUpdate) {
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/download-quarterly-update`,
      downloadQuarterlyUpdate
    );
  }

  demoRequest(downloadQuarterlyUpdate: Partial<DownloadQuarterlyUpdate>) {
    if (environment.platformId == "latam") {
      downloadQuarterlyUpdate.latam = true;
    }

    if (environment.platformId == "co") {
      downloadQuarterlyUpdate.colombia = true;
    }

    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/demoRequest`,
      downloadQuarterlyUpdate
    );
  }

  mailingRequest(downloadQuarterlyUpdate: Partial<DownloadQuarterlyUpdate>) {
    const langLenguaje = this.translate.currentLang.toLowerCase();
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/mailchimp/${langLenguaje}`,
      downloadQuarterlyUpdate
    );
  }

  requestFileIndex(downloadQuarterlyUpdate: DownloadQuarterlyUpdate) {
    if (environment.platformId == "latam") {
      downloadQuarterlyUpdate.latam = true;
    }

    if (environment.platformId == "co") {
      downloadQuarterlyUpdate.colombia = true;
    }

    downloadQuarterlyUpdate.lang = this.lang;
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/download-index`,
      downloadQuarterlyUpdate
    );
  }

  downloadPdf(filePath: string) {
    const link = document.createElement("a");
    link.href = filePath;
    link.target = "_blank";
    link.click();
  }

  getLangSelector() {
    return this.translate.currentLang === "pt"
      ? "pt-br"
      : this.translate.currentLang;
  }

  infoRequest(downloadQuarterlyUpdate: Partial<DownloadQuarterlyUpdate>) {
    if (environment.platformId == "latam") {
      downloadQuarterlyUpdate.latam = true;
    }

    if (environment.platformId == "co") {
      downloadQuarterlyUpdate.colombia = true;
    }
    return this.http.post<DownloadQuarterlyResponse>(
      `${environment.apiURL}/info/infoRequest `,
      downloadQuarterlyUpdate
    );
  }

  sendEmail(data: MailingListRequest) {
    const params: MailingListRequest = { ...data };
    if (environment.platformId == "latam") params.latam = true;
    if (environment.platformId == "co") params.colombia = true;
    return this.http.post<any>(
      `${environment.apiURL}/info/mailing-list`,
      params
    );
  }
}
